import { useState } from "react";
import {
  EpisodesTable as EpisodesTableView,
  type EpisodeFilter,
  type EpisodeSort,
} from "@softmax/product-ux/play";
import { formatScore } from "@softmax/product-ux/play-values";
import { median } from "../lib/episodes/episode-scores";
import {
  activityTimeZone,
  formatRelative,
} from "../lib/activity/feed-calendar";
import type { EpisodeRow, EpisodesSummary } from "../lib/types";

export function EpisodesTable({
  rows,
  summary,
  now,
  allEpisodesHref,
  maxRows = 5,
}: {
  readonly rows: readonly EpisodeRow[];
  readonly summary: EpisodesSummary;
  readonly now: string;
  readonly allEpisodesHref: string | null;
  readonly maxRows?: number;
}) {
  const [filter, setFilter] = useState<EpisodeFilter>("all");
  const [sort, setSort] = useState<EpisodeSort>("newest");
  const topTenCount = rows.filter((row) => row.topTenPercent).length;
  const filters: Record<
    EpisodeFilter,
    {
      readonly key: EpisodeFilter;
      readonly label: string;
      readonly total: number;
      readonly noun: string;
    }
  > = {
    all: {
      key: "all",
      label: `All ${summary.loaded}`,
      total: summary.loaded,
      noun: "all",
    },
    won: {
      key: "won",
      label: `Won ${summary.won}`,
      total: summary.won,
      noun: "won",
    },
    lost: {
      key: "lost",
      label: `Lost ${summary.lost}`,
      total: summary.lost,
      noun: "lost",
    },
    top10: {
      key: "top10",
      label: `Top 10 % ${topTenCount}`,
      total: topTenCount,
      noun: "top 10 %",
    },
  };
  const activeFilter =
    summary.hasWinSignal || (filter !== "won" && filter !== "lost")
      ? filter
      : "all";
  const filtered = rows.filter((row) => {
    switch (activeFilter) {
      case "all":
        return true;
      case "won":
        return row.won === true;
      case "lost":
        return row.won === false;
      case "top10":
        return row.topTenPercent;
    }
  });
  const sorted =
    sort === "score"
      ? filtered.toSorted((a, b) => b.score - a.score)
      : filtered;
  const selected = filters[activeFilter];
  const filteredMedian = filtered.length
    ? median(filtered.map((row) => row.score))
    : null;
  const footer =
    activeFilter === "all"
      ? `${summary.hasWinSignal ? `${summary.won} won · ` : ""}median score ${formatScore(summary.medianScore)}`
      : `${selected.total} ${selected.noun} · median score ${formatScore(filteredMedian)} (${selected.noun} only)`;

  return (
    <EpisodesTableView
      rows={sorted.slice(0, maxRows).map((row) => ({
        ...row,
        when: formatRelative({
          iso: row.createdAt,
          now,
          timeZone: activityTimeZone,
        }),
      }))}
      filters={Object.values(filters).filter(
        (option) =>
          summary.hasWinSignal ||
          (option.key !== "won" && option.key !== "lost"),
      )}
      activeFilter={activeFilter}
      sort={sort}
      onFilterChange={setFilter}
      onSortChange={setSort}
      footer={footer}
      allEpisodesHref={allEpisodesHref}
    />
  );
}
